import React from 'react'

import { useDashboard } from '@/hooks'
import { getSpeedUnit, kphToUser, SpeedUnit, userToKph } from '@/utils/conversions'

interface SpeedInputProps {
  label: string
  value: string | number
  max?: number
  min?: number
  defaultValue?: number
  placeholder?: string
  onChange: (value?: string | number) => void
}

const toUserSpeed = (units: SpeedUnit, speed?: number) => (speed !== undefined ? kphToUser(speed, units) : undefined)

const SpeedInput = ({
  label,
  value,
  placeholder = 'Enter a speed value...',
  onChange,
  max,
  min,
  defaultValue,
}: SpeedInputProps) => {
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const speedUnit = getSpeedUnit(units)

  const userMaxValue = toUserSpeed(speedUnit, max)
  const userMinValue = toUserSpeed(speedUnit, min)
  const userDefaultValue = value ? kphToUser(Number(value), speedUnit) : kphToUser(Number(defaultValue), speedUnit)

  const [inputValue, setInputValue] = React.useState<number | undefined>(userDefaultValue ?? 0)

  const handleChange = (value?: string | number) => {
    const speedValue = userToKph(Number(value), speedUnit)
    onChange(Math.round(speedValue))
  }

  return (
    <div className="mb-4">
      <label className="block text-base-content mb-2">
        {label} ({speedUnit})
      </label>
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          type="number"
          max={userMaxValue}
          min={userMinValue}
          className="input input-bordered text-base border-base-300 w-[53%] focus:outline-none focus:border-primary/30"
          value={inputValue ? Math.round(inputValue) : ''}
          onChange={(e) => {
            // Only update the input state while typing
            const value = e.target.value ? parseInt(e.target.value, 10) : 0
            setInputValue(value)
          }}
          onBlur={() => {
            if (!inputValue) {
              handleChange(undefined)
              return
            }

            let newValue = inputValue

            if (Number.isNaN(newValue)) {
              handleChange(undefined)
              return
            }

            if (userMaxValue !== undefined && newValue > userMaxValue) {
              newValue = userMaxValue
              setInputValue(newValue)
            }

            if (userMinValue !== undefined && newValue < userMinValue) {
              newValue = userMinValue
              setInputValue(newValue)
            }

            handleChange(newValue)
          }}
          placeholder={placeholder}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
        />
      </div>
    </div>
  )
}

export default SpeedInput
